// Public marketing landing — chitdesk.com / www. Editorial-fintech aesthetic:
// warm cream canvas with forest-green + gold, a Fraunces display serif for
// English headlines, an inline product mockup as the hero centerpiece, a bento
// feature grid, and an orchestrated staggered page-load. Fully static (no API),
// bilingual (Tamil default). CTAs hand off to chitos.<apex>/signup + /login.

import { useEffect, useState, type ReactElement } from 'react';
import { use_language, set_language, font_class_for } from '../i18n/index.js';
import { Icon } from '../components/Icon.js';
import { master_app_url } from '../lib/marketing.js';
import './landing.css';

const GOLD = '#9A6B12';

// ─── Brand mark — forest-green tile holding a Tamil glyph ────────────────────

function BrandMark({ size = 34 }: { size?: number }): ReactElement {
  return (
    <span
      className="inline-grid place-items-center rounded-[0.4em] bg-primary text-white font-tamil leading-none shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.5 }}
      aria-hidden
    >
      சீ
    </span>
  );
}

// ─── Product mockup — a stylised ChitDesk operator dashboard ───────────────────

function DashboardMock({ ta }: { ta: boolean }): ReactElement {
  return (
    <div className="lp-window rounded-2xl bg-surface border border-line overflow-hidden">
      {/* window chrome */}
      <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-line bg-bg-elev">
        <span className="flex gap-1.5">
          <i className="w-2.5 h-2.5 rounded-full" style={{ background: '#E0B9B0' }} />
          <i className="w-2.5 h-2.5 rounded-full" style={{ background: '#E7D2A6' }} />
          <i className="w-2.5 h-2.5 rounded-full" style={{ background: '#BFD6C0' }} />
        </span>
        <div className="ml-1.5 flex items-center gap-1.5">
          <BrandMark size={16} />
          <span className="font-tamil text-[12px] text-primary-ink">ரமேஷ் சீட்ஸ்</span>
        </div>
        <span className="ml-auto inline-grid place-items-center w-5 h-5 rounded-full bg-primary-soft text-primary text-[9px] font-semibold">RC</span>
      </div>
      <div className="flex">
        {/* rail */}
        <div className="hidden sm:flex flex-col items-center gap-3 py-4 px-2.5 border-r border-line bg-bg-elev/60">
          {['home', 'cash', 'chits', 'people', 'reports'].map((k, i) => (
            <span key={k} className={`inline-grid place-items-center w-7 h-7 rounded-lg ${i === 0 ? 'bg-primary text-white' : 'text-ink-3'}`}>
              {(() => { const I = Icon[k as keyof typeof Icon]; return <I />; })()}
            </span>
          ))}
        </div>
        {/* content */}
        <div className="flex-1 p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <div className={ta ? 'font-tamil' : ''}>
              <div className="text-[13px] font-semibold text-ink">{ta ? 'முகப்பு' : 'Dashboard'}</div>
              <div className="text-[10px] text-ink-3">{ta ? 'இன்று · 09 ஜூன்' : 'Today · 09 Jun'}</div>
            </div>
            <span className="text-[10px] px-2 py-1 rounded-md bg-primary-soft text-primary-ink font-medium">{ta ? 'வசூல் நாள்' : 'Collection day'}</span>
          </div>
          {/* KPI cards */}
          <div className="grid grid-cols-3 gap-2.5">
            {[
              { label: ta ? 'இன்று வசூல்' : 'Collected today', value: '₹29,000', accent: true },
              { label: ta ? 'செயலில் சீட்டுகள்' : 'Active chits', value: '12' },
              { label: ta ? 'உறுப்பினர்கள்' : 'Members', value: '240' },
            ].map((c) => (
              <div key={c.label} className={`rounded-lg border px-2.5 py-2 ${c.accent ? 'border-primary-soft bg-primary-soft/40' : 'border-line bg-bg'}`}>
                <div className={`text-[8.5px] uppercase tracking-wide text-ink-3 mb-0.5 ${ta ? 'font-tamil' : ''}`} style={{ letterSpacing: '0.02em' }}>{c.label}</div>
                <div className={`text-[15px] font-semibold mono ${c.accent ? 'text-primary' : 'text-ink'}`}>{c.value}</div>
              </div>
            ))}
          </div>
          {/* bar chart */}
          <div className="mt-4 rounded-lg border border-line bg-bg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className={`text-[10px] text-ink-2 ${ta ? 'font-tamil' : ''}`}>{ta ? 'மாதாந்திர வசூல்' : 'Monthly collection'}</span>
              <span className="text-[10px] mono" style={{ color: GOLD }}>94%</span>
            </div>
            <div className="flex items-end gap-1.5 h-16">
              {[42, 58, 50, 66, 72, 60, 80, 74, 90, 84].map((h, i) => (
                <span key={i} className={`flex-1 ${i >= 8 ? 'lp-bar-gold' : i % 4 === 1 ? 'lp-bar-soft' : 'lp-bar'}`} style={{ height: `${h}%`, borderRadius: '3px 3px 0 0' }} />
              ))}
            </div>
          </div>
          {/* chit row */}
          <div className="mt-3 rounded-lg border border-line bg-surface px-3 py-2.5 flex items-center gap-3">
            <span className="inline-grid place-items-center w-7 h-7 rounded-lg bg-primary-soft text-primary"><Icon.chits /></span>
            <div className="flex-1 min-w-0">
              <div className={`text-[12px] font-medium text-ink ${ta ? 'font-tamil' : ''}`}>{ta ? 'கோல்டு 1' : 'Gold 1'} <span className="text-ink-3 font-normal">· ₹1,00,000</span></div>
              <div className="mt-1 h-1.5 rounded-full bg-bg-elev overflow-hidden">
                <span className="block h-full rounded-full bg-primary" style={{ width: '29%' }} />
              </div>
            </div>
            <span className="text-[11px] mono text-ink-3">29%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function WhatsAppBubble({ ta }: { ta: boolean }): ReactElement {
  return (
    <div className="rounded-xl border border-line bg-surface px-3 py-2.5 lp-window" style={{ maxWidth: 232 }}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="inline-grid place-items-center w-4 h-4 rounded-full bg-[#25D366] text-white"><Icon.wa /></span>
        <span className="text-[10px] text-ink-3">{ta ? 'வாட்ஸ்அப் · இப்போது' : 'WhatsApp · now'}</span>
      </div>
      <div className="rounded-lg rounded-tl-sm bg-primary-soft/70 px-2.5 py-2">
        <p className="font-tamil text-[11.5px] leading-snug text-primary-ink">வணக்கம் முருகன், நாளை கோல்டு 1 சந்தா ₹5,000. தயவு செய்து நேரத்தில் செலுத்தவும். 🙏</p>
        <div className="flex items-center justify-end gap-1 mt-1 text-[9px]" style={{ color: '#2F6B47' }}>
          <span>09:00</span><span>✓✓</span>
        </div>
      </div>
    </div>
  );
}

// ─── Content (bilingual) ─────────────────────────────────────────────────────

const STATS: Array<{ value: string; label_en: string; label_ta: string }> = [
  { value: '100%', label_en: 'Tamil-first', label_ta: 'தமிழ் முதல்' },
  { value: '8', label_en: 'ready reports', label_ta: 'தயார் அறிக்கைகள்' },
  { value: '3', label_en: 'ways to record dues', label_ta: 'நிலுவை பதிவு வழிகள்' },
  { value: '2 min', label_en: 'to go live', label_ta: 'அமைக்க' },
];

interface FlipRow { old_en: string; old_ta: string; new_en: string; new_ta: string }
const FLIP_ROWS: FlipRow[] = [
  { old_en: '~120 reminders typed by hand each month', old_ta: 'மாதம் ~120 நினைவூட்டல்கள் கையால்', new_en: 'Sent automatically, in Tamil, on schedule', new_ta: 'தமிழில், திட்டப்படி, தானாக' },
  { old_en: 'Chasing members for dues, door to door', old_ta: 'வீடு வீடாக பணம் கேட்டு அலைதல்', new_en: 'Record cash, UPI or cheque in seconds', new_ta: 'ரொக்கம், UPI, காசோலை — விநாடிகளில் பதிவு' },
  { old_en: 'Excel sheets that never reconcile', old_ta: 'பொருந்தாத Excel தாள்கள்', new_en: 'Cashbook + bank close cleanly nightly', new_ta: 'பணப்புத்தகம் + வங்கி தினமும் சரி' },
  { old_en: 'Hours rebuilding statements for the CA', old_ta: 'CA-க்கு மணிக்கணக்கில் அறிக்கை', new_en: 'GST / TDS statements in one click', new_ta: 'GST / TDS அறிக்கை ஒரே கிளிக்' },
];

interface Feature { icon: keyof typeof Icon; title_en: string; title_ta: string; body_en: string; body_ta: string; wide?: boolean }
const FEATURES: Feature[] = [
  { icon: 'wa', wide: true, title_en: 'WhatsApp does the talking', title_ta: 'WhatsApp பேசுகிறது', body_en: 'Due reminders, receipts, overdue nudges and payout-ready alerts go out automatically over the WhatsApp Business API — every routine touchpoint, in Tamil, with zero manual work.', body_ta: 'நினைவூட்டல், ரசீது, தாமத எச்சரிக்கை, பணம் தயார் அறிவிப்பு — WhatsApp வணிக API மூலம் தமிழில் தானாக.' },
  { icon: 'upi', title_en: 'Record any payment', title_ta: 'எந்தக் கட்டணமும் பதிவு', body_en: 'Log every collection by cash, UPI or cheque in seconds — attach a UPI screenshot as proof. Works offline and syncs when you are back online.', body_ta: 'ரொக்கம், UPI, காசோலை — ஒவ்வொரு வசூலையும் விநாடிகளில் பதிவு செய்யுங்கள்; UPI ஸ்கிரீன்ஷாட்டை சான்றாக இணைக்கலாம். இணையம் இல்லாமலும் வேலை செய்யும்.' },
  { icon: 'doc', title_en: 'Tamil-first', title_ta: 'தமிழ் முதல்', body_en: 'Names, receipts, voice calls — Tamil by default. English is the optional second layer.', body_ta: 'பெயர், ரசீது, குரல் அழைப்பு — இயல்பாக தமிழில். ஆங்கிலம் விருப்பம்.' },
  { icon: 'cash', title_en: 'Books that close daily', title_ta: 'தினமும் மூடும் புத்தகம்', body_en: 'Cashbook, bank reconciliation and a day book — one ledger that closes cleanly every evening.', body_ta: 'பணப்புத்தகம், வங்கி ஒப்பீடு, தினசரி கணக்கு — ஒரே பேரேடு, தினமும் சரி.' },
  { icon: 'reports', title_en: 'Reports your CA files', title_ta: 'CA தாக்கல் அறிக்கை', body_en: 'Eight operator reports plus GST, TDS and annual statements in the format your accountant uses.', body_ta: 'எட்டு அறிக்கைகள் + GST, TDS, ஆண்டு அறிக்கை — CA வடிவில்.' },
];

interface Step { n: string; title_en: string; title_ta: string; body_en: string; body_ta: string }
const STEPS: Step[] = [
  { n: '01', title_en: 'Create your workspace', title_ta: 'வேலையகத்தை உருவாக்குங்கள்', body_en: 'Pick a plan, enter your business details. Your private, isolated workspace is live in under two minutes.', body_ta: 'திட்டத்தைத் தேர்ந்து விவரம் நிரப்புங்கள். இரண்டே நிமிடத்தில் தனி வேலையகம் தயார்.' },
  { n: '02', title_en: 'Run one chit in parallel', title_ta: 'ஒரு சீட்டை இணையாக நடத்துங்கள்', body_en: 'Move a single chit over and run it alongside your paper book for a month. Nothing to lose.', body_ta: 'ஒரு சீட்டை பதிவேட்டுடன் இணையாக ஒரு மாதம் நடத்துங்கள். இழக்க எதுவுமில்லை.' },
  { n: '03', title_en: 'Let it run the routine', title_ta: 'வேலையை அதனிடம் விடுங்கள்', body_en: 'WhatsApp reminders go out, you record payments as they come in, the books close — you just review.', body_ta: 'WhatsApp நினைவூட்டல் செல்கிறது, வரும் கட்டணங்களைப் பதிவு செய்கிறீர்கள், புத்தகம் மூடுகிறது — நீங்கள் பார்வையிட்டால் போதும்.' },
];

// Pricing tiers — the customer-facing mirror of TIER_ENTITLEMENTS
// (@chitos/types). `monthly` is rupees; yearly = 10× (two months free).
interface Tier { name_en: string; name_ta: string; monthly: number; tagline_en: string; tagline_ta: string; features_en: string[]; features_ta: string[]; highlight?: boolean }
const TIERS: Tier[] = [
  { name_en: 'Starter', name_ta: 'தொடக்கம்', monthly: 500, tagline_en: 'Solo operators, a handful of chits.', tagline_ta: 'சில சீட்டுகள், தனி நிறுவனர்.', features_en: ['Up to 5 active chits', 'Own-number WhatsApp + per-chit groups', 'Subscriber portal & receipts', 'Collection-day & member reports', 'Tamil + English'], features_ta: ['5 சீட்டுகள் வரை', 'சொந்த எண் WhatsApp + சீட்டு குழுக்கள்', 'உறுப்பினர் போர்டல் & ரசீதுகள்', 'வசூல்நாள் & உறுப்பினர் அறிக்கைகள்', 'தமிழ் + ஆங்கிலம்'] },
  { name_en: 'Growth', name_ta: 'வளர்ச்சி', monthly: 1500, tagline_en: 'Scaling across branches.', tagline_ta: 'பல கிளைகளாக விரிவு.', features_en: ['Everything in Starter, plus:', 'Up to 25 active chits', 'Official WhatsApp, SMS & voice reminders', 'Ledger-photo import + bank reconciliation', 'Custom domain & branding', 'All reports + tax & Tally export'], features_ta: ['தொடக்கத்தில் உள்ள அனைத்தும், மேலும்:', '25 சீட்டுகள் வரை', 'அதிகாரப்பூர்வ WhatsApp, SMS & குரல் நினைவூட்டல்', 'பதிவேடு-புகைப்பட இறக்குமதி + வங்கி சரிகட்டல்', 'சொந்த டொமைன் & பிராண்டிங்', 'அனைத்து அறிக்கைகள் + வரி & Tally ஏற்றுமதி'], highlight: true },
  { name_en: 'Scale', name_ta: 'அளவீடு', monthly: 3000, tagline_en: 'Established, high volume.', tagline_ta: 'நிலையான, அதிக அளவு.', features_en: ['Everything in Growth, plus:', 'Unlimited chits & members', 'KYC OCR — Aadhaar / PAN / cheque', 'AI risk & credit scoring', 'API access + priority support', 'All 5 languages'], features_ta: ['வளர்ச்சியில் உள்ள அனைத்தும், மேலும்:', 'வரம்பற்ற சீட்டுகள் & உறுப்பினர்கள்', 'KYC OCR — ஆதார் / PAN / காசோலை', 'AI ஆபத்து & கடன் மதிப்பீடு', 'API அணுகல் + முன்னுரிமை ஆதரவு', 'அனைத்து 5 மொழிகள்'] },
];

// Feature comparison matrix — rendered below the cards. 1 = yes, 0 = no.
interface CompareRow { label_en: string; label_ta: string; cells: [string | 1 | 0, string | 1 | 0, string | 1 | 0] }
const COMPARE: CompareRow[] = [
  { label_en: 'Active chits', label_ta: 'செயல் சீட்டுகள்', cells: ['5', '25', '∞'] },
  { label_en: 'Members', label_ta: 'உறுப்பினர்கள்', cells: ['50', '500', '∞'] },
  { label_en: 'Staff logins', label_ta: 'பணியாளர் உள்நுழைவு', cells: ['1', '5', '∞'] },
  { label_en: 'Own-number WhatsApp + groups', label_ta: 'சொந்த எண் WhatsApp + குழுக்கள்', cells: [1, 1, 1] },
  { label_en: 'Subscriber portal, receipts, cash book', label_ta: 'உறுப்பினர் போர்டல், ரசீது, ரொக்கப் புத்தகம்', cells: [1, 1, 1] },
  { label_en: 'Official WhatsApp, SMS & voice', label_ta: 'அதிகாரப்பூர்வ WhatsApp, SMS & குரல்', cells: [0, 1, 1] },
  { label_en: 'Ledger-photo import (AI)', label_ta: 'பதிவேடு-புகைப்பட இறக்குமதி (AI)', cells: [0, 1, 1] },
  { label_en: 'Bank reconciliation', label_ta: 'வங்கி சரிகட்டல்', cells: [0, 1, 1] },
  { label_en: 'Payment screenshot capture', label_ta: 'கட்டண ஸ்கிரீன்ஷாட் பதிவு', cells: [1, 1, 1] },
  { label_en: 'Custom domain + branding', label_ta: 'சொந்த டொமைன் + பிராண்டிங்', cells: [0, 1, 1] },
  { label_en: 'All reports + tax & Tally export', label_ta: 'அனைத்து அறிக்கை + வரி & Tally', cells: [0, 1, 1] },
  { label_en: 'KYC OCR (Aadhaar / PAN / cheque)', label_ta: 'KYC OCR (ஆதார் / PAN / காசோலை)', cells: [0, 0, 1] },
  { label_en: 'AI risk & credit scoring', label_ta: 'AI ஆபத்து & கடன் மதிப்பீடு', cells: [0, 0, 1] },
  { label_en: 'API access', label_ta: 'API அணுகல்', cells: [0, 0, 1] },
  { label_en: 'Priority support', label_ta: 'முன்னுரிமை ஆதரவு', cells: [0, 0, 1] },
  { label_en: 'Languages', label_ta: 'மொழிகள்', cells: ['TA + EN', 'TA + EN', 'All 5'] },
];

interface Faq { q_en: string; q_ta: string; a_en: string; a_ta: string }
const FAQS: Faq[] = [
  { q_en: 'Is my members’ data safe?', q_ta: 'என் உறுப்பினர் தரவு பாதுகாப்பானதா?', a_en: 'Every operator gets a fully isolated database, storage bucket and API address — no shared rows, no cross-tenant access. Every change is written to an audit trail.', a_ta: 'ஒவ்வொரு நிறுவனருக்கும் முற்றிலும் தனி தரவுத்தளம், storage, API முகவரி — பகிர்வு இல்லை. ஒவ்வொரு மாற்றமும் தடயப்பதிவில்.' },
  { q_en: 'Can I try it without leaving my paper book?', q_ta: 'பதிவேட்டை விடாமல் முயற்சிக்கலாமா?', a_en: 'Yes. Run one chit in parallel for a month and switch fully only once you trust it.', a_ta: 'ஆம். ஒரு சீட்டை ஒரு மாதம் இணையாக நடத்துங்கள்; நம்பிக்கை வந்த பிறகே மாறுங்கள்.' },
  { q_en: 'Does it really work in Tamil?', q_ta: 'இது உண்மையில் தமிழில் வேலை செய்யுமா?', a_en: 'Tamil is the default everywhere — names, receipts, WhatsApp, even voice reminders. English is an optional toggle.', a_ta: 'எங்கும் தமிழே இயல்பு — பெயர், ரசீது, WhatsApp, குரல் நினைவூட்டல் கூட. ஆங்கிலம் விருப்பம்.' },
  { q_en: 'I run a fixed-order chit, no auction. Will it fit?', q_ta: 'முன்வரிசை சீட்டு, ஏலம் இல்லை. பொருந்துமா?', a_en: 'That is exactly what ChitDesk is built for — pre-assigned payout order, a per-cycle schedule, and a collection ledger. No auction mechanics.', a_ta: 'சிட்டெஸ்க் அதற்காகவே — முன்வரிசை பணம், சுழற்சி அட்டவணை, வசூல் பேரேடு. ஏலம் இல்லை.' },
  { q_en: 'What about GST and TDS?', q_ta: 'GST மற்றும் TDS பற்றி?', a_en: 'Built in. GST-ready exports, TDS captures on prize money and annual statements — in the format your CA files.', a_ta: 'உள்ளமைப்பு. GST ஏற்றுமதி, பரிசுப் பணத்தில் TDS, ஆண்டு அறிக்கை — CA வடிவில்.' },
  { q_en: 'How do I log in after signing up?', q_ta: 'பதிவு செய்த பிறகு எப்படி உள்நுழைவது?', a_en: 'Your workspace gets its own private address — we email it to you the moment it is ready (e.g. rameshchits.chitdesk.com). Bookmark it and sign in there; there is no shared login.', a_ta: 'உங்கள் வேலையகத்திற்கு சொந்த முகவரி கிடைக்கும் (எ.கா. rameshchits.chitdesk.com) — தயாரானவுடன் மின்னஞ்சலில் அனுப்புவோம். அதை சேமித்து அங்கேயே உள்நுழையுங்கள்.' },
];

// ─── Page ───────────────────────────────────────────────────────────────────

export function LandingPage(): ReactElement {
  const lang = use_language();
  const ta = lang === 'ta';
  const tf = font_class_for(lang);
  const t = (en: string, tam: string): string => (ta ? tam : en);
  const dsp = ta ? 'font-tamil' : 'lp-display'; // display headline font

  const signup_url = master_app_url('/signup');
  const [yearly, set_yearly] = useState(false);
  // Yearly = 10× monthly (two months free). Indian-grouped rupee string.
  const price_for = (monthly: number): string =>
    '₹' + (yearly ? monthly * 10 : monthly).toLocaleString('en-IN');
  const scroll_to = (id: string) => (e: { preventDefault: () => void }) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Marketing-only document title (the shared index.html title stays "ChitDesk"
  // for in-app tabs; this enriches just the public landing for SEO + the tab).
  useEffect(() => {
    document.title = 'ChitDesk — chit fund software for Tamil Nadu';
  }, []);

  return (
    <main className="lp lp-canvas lp-grain min-h-screen text-ink antialiased overflow-x-hidden">
      {/* ── Nav ── */}
      <header className="sticky top-0 z-40 border-b border-line/70" style={{ background: 'rgba(246,242,236,0.72)', backdropFilter: 'saturate(140%) blur(10px)' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between px-5 sm:px-6 h-16">
          <a href="/" className="flex items-center gap-2.5 no-underline">
            <BrandMark size={36} />
            <span className="flex flex-col leading-none">
              <span className="text-lg font-semibold tracking-tight text-primary-ink">ChitDesk</span>
              <span className="lp-eyebrow text-ink-3" style={{ fontSize: 9 }}>{t('Chit fund software', 'சீட்டு மென்பொருள்')}</span>
            </span>
          </a>
          <nav className="flex items-center gap-1 sm:gap-2">
            <a href="#pricing" onClick={scroll_to('pricing')} className={`hidden sm:inline-block px-3.5 py-2 rounded-lg text-sm text-ink-2 hover:text-primary hover:bg-bg-elev transition no-underline ${tf}`}>{t('Pricing', 'கட்டணம்')}</a>
            <a href="/docs" className={`hidden sm:inline-block px-3.5 py-2 rounded-lg text-sm text-ink-2 hover:text-primary hover:bg-bg-elev transition no-underline ${tf}`}>{t('Docs', 'ஆவணங்கள்')}</a>
            <button type="button" onClick={() => set_language(ta ? 'en' : 'ta')} className="px-3 py-2 rounded-lg text-sm text-ink-2 hover:text-primary hover:bg-bg-elev transition">{ta ? 'English' : 'தமிழ்'}</button>
            <a href={signup_url} className={`lp-cta px-4 py-2 rounded-lg text-sm font-medium bg-primary text-white no-underline ${tf}`}>{t('Get started', 'தொடங்கு')}</a>
          </nav>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="relative max-w-6xl mx-auto px-5 sm:px-6 pt-14 sm:pt-20 pb-12">
        <div className="lp-aura" style={{ width: 520, height: 420, right: -60, top: -40, background: 'radial-gradient(closest-side, rgba(46,95,61,0.28), transparent)' }} />
        <div className="grid lg:grid-cols-[1.04fr_0.96fr] gap-12 lg:gap-10 items-center">
          {/* copy */}
          <div>
            <div className="lp-rise lp-d1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-line bg-surface/70 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span className={`lp-eyebrow text-ink-2`}>{t('For Tamil Nadu chit-fund operators', 'தமிழ்நாடு சீட்டு நிறுவனர்களுக்காக')}</span>
            </div>
            <h1 className={`lp-rise lp-d2 ${dsp} text-[2.6rem] leading-[1.04] sm:text-6xl sm:leading-[1.02] text-primary-ink`} style={{ fontWeight: 600 }}>
              {ta ? (
                <>உங்கள் சீட்டு வணிகத்திற்கு <span className="text-primary">தகுந்த</span> நவீன மென்பொருள்.</>
              ) : (
                <>The software your chit fund <span className="lp-display-it text-primary">finally</span> deserves.</>
              )}
            </h1>
            <p className={`lp-rise lp-d3 mt-6 text-base sm:text-lg text-ink-2 max-w-xl leading-relaxed ${tf}`}>
              {ta
                ? 'தமிழ் முதல் · WhatsApp தானியங்கி · தினமும் மூடும் கணக்கு — ஒவ்வொரு நிறுவனருக்கும் தனி, பாதுகாக்கப்பட்ட வேலையகம். காகிதத்தை விட்டு, கட்டிய நம்பிக்கையை இழக்காமல் நகருங்கள்.'
                : 'Tamil-first, WhatsApp-native and books that close daily — with a private, isolated workspace for every operator. Move off paper without losing the trust you’ve built.'}
            </p>
            <div className="lp-rise lp-d4 mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
              <a href={signup_url} className={`lp-cta inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-primary text-white font-medium text-sm no-underline ${tf}`}>
                {t('Create your workspace', 'வேலையகத்தை உருவாக்குங்கள்')}<Icon.chevR />
              </a>
              <a href="#pricing" onClick={scroll_to('pricing')} className={`inline-flex items-center justify-center px-6 py-3.5 rounded-xl border border-line bg-surface hover:border-primary text-ink-2 hover:text-primary font-medium text-sm transition no-underline ${tf}`}>
                {t('See pricing', 'கட்டணத்தைப் பார்க்க')}
              </a>
            </div>
            <p className={`lp-rise lp-d5 mt-4 text-xs text-ink-3 ${tf}`}>{t('Live in two minutes · Keep your paper book as long as you like', 'இரண்டே நிமிடத்தில் தயார் · பதிவேட்டை எவ்வளவு காலமும் வைத்திருங்கள்')}</p>
          </div>
          {/* product mock */}
          <div className="relative lp-mock">
            <DashboardMock ta={ta} />
            <div className="absolute -bottom-7 -left-6 lg:-left-12 hidden lg:block" style={{ animation: 'lp-float 8s ease-in-out 1.6s infinite' }}>
              <WhatsAppBubble ta={ta} />
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats strip ── */}
      <section className="border-y border-line bg-surface/60">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-7 grid grid-cols-2 sm:grid-cols-4 gap-y-6">
          {STATS.map((s) => (
            <div key={s.label_en} className="text-center sm:border-r last:border-r-0 border-line/70">
              <div className="lp-display text-3xl sm:text-4xl text-primary-ink" style={{ fontWeight: 600 }}>{s.value}</div>
              <div className={`mt-1 text-xs sm:text-sm text-ink-3 ${tf}`}>{ta ? s.label_ta : s.label_en}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Pain-flip ── */}
      <section className="max-w-5xl mx-auto px-5 sm:px-6 py-20 sm:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <div className="lp-eyebrow text-primary mb-3">{t('Before / After', 'முன்பு / பின்பு')}</div>
          <h2 className={`${dsp} text-3xl sm:text-4xl text-primary-ink`} style={{ fontWeight: 600 }}>{t('The same chit business — without the grind.', 'அதே சீட்டு வணிகம் — அலைச்சல் இல்லாமல்.')}</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 gap-px rounded-2xl overflow-hidden border border-line bg-line">
          <div className="bg-bg-elev p-6 sm:p-8">
            <div className={`flex items-center gap-2 text-sm font-medium mb-5 ${tf}`} style={{ color: '#9C2A1B' }}><Icon.close />{t('On paper, today', 'காகிதத்தில், இன்று')}</div>
            <ul className="grid gap-4">
              {FLIP_ROWS.map((r, i) => (
                <li key={i} className={`flex items-start gap-3 text-sm text-ink-2 ${tf}`}>
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: '#C98B80' }} />
                  {ta ? r.old_ta : r.old_en}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-surface p-6 sm:p-8">
            <div className={`flex items-center gap-2 text-sm font-medium text-primary mb-5 ${tf}`}><Icon.check />{t('With ChitDesk', 'சிட்டெஸ்க் உடன்')}</div>
            <ul className="grid gap-4">
              {FLIP_ROWS.map((r, i) => (
                <li key={i} className={`flex items-start gap-3 text-sm text-ink ${tf}`}>
                  <span className="mt-0.5 text-primary shrink-0"><Icon.check /></span>
                  {ta ? r.new_ta : r.new_en}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── Features — bento ── */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 pb-4">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="lp-eyebrow text-primary mb-3">{t('What it runs', 'என்ன இயக்குகிறது')}</div>
          <h2 className={`${dsp} text-3xl sm:text-4xl text-primary-ink`} style={{ fontWeight: 600 }}>{t('Everything a chit office runs on.', 'சீட்டு அலுவலகம் இயங்க தேவையான அனைத்தும்.')}</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 auto-rows-fr">
          {FEATURES.map((f) => {
            const I = Icon[f.icon];
            return (
              <article key={f.title_en} className={`lp-card rounded-2xl border border-line bg-surface p-6 flex flex-col ${f.wide ? 'sm:col-span-2 lg:row-span-1' : ''}`}>
                <span className="inline-grid place-items-center w-11 h-11 rounded-xl bg-primary-soft text-primary shrink-0 mb-4"><I /></span>
                <h3 className={`text-base font-semibold text-ink mb-2 ${ta ? 'font-tamil' : ''}`}>{ta ? f.title_ta : f.title_en}</h3>
                <p className={`text-sm text-ink-2 leading-relaxed ${tf}`}>{ta ? f.body_ta : f.body_en}</p>
                {f.wide && (
                  <div className="mt-5 self-start"><WhatsAppBubble ta={ta} /></div>
                )}
              </article>
            );
          })}
          {/* CTA tile */}
          <article className="rounded-2xl border border-primary bg-primary-ink text-white p-6 flex flex-col justify-between">
            <div>
              <span className="inline-grid place-items-center w-11 h-11 rounded-xl bg-white/10 mb-4"><Icon.shield /></span>
              <h3 className={`text-base font-semibold mb-2 ${ta ? 'font-tamil' : ''}`}>{t('Your own private vault', 'உங்கள் சொந்த பெட்டகம்')}</h3>
              <p className={`text-sm text-white/75 leading-relaxed ${tf}`}>{t('Own database, own storage, own API host. No two operators ever share a row.', 'சொந்த தரவுத்தளம், storage, API. இரு நிறுவனர்களின் தரவு கலக்காது.')}</p>
            </div>
            <a href={signup_url} className={`mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary-soft no-underline hover:gap-2.5 transition-all ${tf}`}>{t('Get started', 'தொடங்கு')}<Icon.chevR /></a>
          </article>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="max-w-5xl mx-auto px-5 sm:px-6 py-20 sm:py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="lp-eyebrow text-primary mb-3">{t('Getting started', 'தொடங்குதல்')}</div>
          <h2 className={`${dsp} text-3xl sm:text-4xl text-primary-ink`} style={{ fontWeight: 600 }}>{t('Switch over in three steps.', 'மூன்று படிகளில் மாறுங்கள்.')}</h2>
        </div>
        <div className="grid gap-10 md:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.n} className="relative">
              <div className="lp-display text-5xl text-primary-soft mb-3" style={{ fontWeight: 600 }}>{s.n}</div>
              <h3 className={`text-lg font-semibold text-ink mb-2 ${ta ? 'font-tamil' : ''}`}>{ta ? s.title_ta : s.title_en}</h3>
              <p className={`text-sm text-ink-2 leading-relaxed ${tf}`}>{ta ? s.body_ta : s.body_en}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Security band ── */}
      <section className="relative overflow-hidden bg-primary-ink text-white">
        <div className="lp-aura" style={{ width: 520, height: 420, left: '40%', top: -120, background: 'radial-gradient(closest-side, rgba(63,122,82,0.5), transparent)' }} />
        <div className="max-w-5xl mx-auto px-5 sm:px-6 py-20 sm:py-24 grid gap-12 md:grid-cols-[1.05fr_1fr] md:items-center">
          <div>
            <div className="lp-eyebrow mb-3" style={{ color: '#BFE0C7' }}>{t('Built to be trusted', 'நம்பிக்கைக்காக கட்டப்பட்டது')}</div>
            <h2 className={`${dsp} text-3xl sm:text-4xl`} style={{ fontWeight: 600 }}>{t('Your vault is yours alone.', 'உங்கள் பெட்டகம் உங்களுக்கு மட்டும்.')}</h2>
            <p className={`mt-5 text-white/75 leading-relaxed max-w-lg ${tf}`}>{ta ? 'பல நிறுவனங்களின் தரவு ஒரே இடத்தில் கலக்காது. உங்கள் நிறுவனத்திற்கு சொந்த தரவுத்தளம், சொந்த storage, சொந்த API முகவரி. ஒவ்வொரு ரூபாயும் தடயப்பதிவில் பதிவாகிறது.' : 'No two operators ever share a row. Your business gets its own database, its own storage and its own API address — and every rupee that moves is written into an audit trail.'}</p>
          </div>
          <ul className="grid gap-3">
            {[
              { en: 'Own isolated database per operator', ta: 'நிறுவனர் வாரியாக தனி தரவுத்தளம்' },
              { en: 'Own storage bucket & secure API host', ta: 'சொந்த storage & பாதுகாப்பான API' },
              { en: 'Full audit trail on every change', ta: 'ஒவ்வொரு மாற்றத்திற்கும் தடயப்பதிவு' },
              { en: 'Registrar / GST / TDS exports built in', ta: 'பதிவாளர் / GST / TDS ஏற்றுமதி' },
            ].map((it) => (
              <li key={it.en} className={`flex items-start gap-3 rounded-xl bg-white/[0.06] border border-white/10 px-4 py-3.5 text-sm ${tf}`}>
                <span className="mt-0.5 shrink-0" style={{ color: '#BFE0C7' }}><Icon.check /></span>
                <span className="text-white/90">{ta ? it.ta : it.en}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="max-w-6xl mx-auto px-5 sm:px-6 py-20 sm:py-24 scroll-mt-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="lp-eyebrow text-primary mb-3">{t('Pricing', 'கட்டணம்')}</div>
          <h2 className={`${dsp} text-3xl sm:text-4xl text-primary-ink`} style={{ fontWeight: 600 }}>{t('Honest pricing, in rupees.', 'நேர்மையான கட்டணம், ரூபாயில்.')}</h2>
          <p className={`mt-3 text-ink-2 ${tf}`}>{t('Paid from day one — no surprises. Billed via UPI AutoPay. Pay yearly and get two months free.', 'முதல் நாளிலிருந்து கட்டணம் — மறைவில்லை. UPI ஆட்டோபே. ஆண்டுக்கட்டணத்தில் இரண்டு மாதம் இலவசம்.')}</p>
          {/* Monthly / yearly toggle */}
          <div className="mt-7 inline-flex items-center gap-1 rounded-full border border-line bg-surface p-1">
            <button type="button" onClick={() => set_yearly(false)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${tf} ${!yearly ? 'bg-primary text-white' : 'text-ink-2 hover:text-primary'}`}>{t('Monthly', 'மாதம்')}</button>
            <button type="button" onClick={() => set_yearly(true)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${tf} ${yearly ? 'bg-primary text-white' : 'text-ink-2 hover:text-primary'}`}>{t('Yearly', 'ஆண்டு')}<span className="ml-1.5 text-[11px] opacity-80">{t('−2 months', '−2 மாதம்')}</span></button>
          </div>
        </div>
        <div className="grid gap-5 md:grid-cols-3 items-start">
          {TIERS.map((tier) => (
            <div key={tier.name_en} className={`lp-card relative flex flex-col rounded-2xl border bg-surface p-7 ${tier.highlight ? 'border-primary shadow-md md:-mt-4 md:mb-4' : 'border-line'}`}>
              {tier.highlight && (
                <span className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-white text-[11px] font-medium ${tf}`}>{t('Most popular', 'பிரபலம்')}</span>
              )}
              <h3 className={`text-lg font-semibold text-primary-ink ${ta ? 'font-tamil' : ''}`}>{ta ? tier.name_ta : tier.name_en}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="lp-display text-4xl text-ink" style={{ fontWeight: 600 }}>{price_for(tier.monthly)}</span>
                <span className={`text-sm text-ink-3 ${tf}`}>{yearly ? t('/yr', '/ஆண்டு') : t('/mo', '/மாதம்')}</span>
              </div>
              <p className={`mt-1 text-[12px] text-ink-3 ${tf}`}>{yearly ? t(`₹${tier.monthly.toLocaleString('en-IN')}/mo billed yearly`, `மாதம் ₹${tier.monthly.toLocaleString('en-IN')}, ஆண்டுக்கட்டணம்`) : t('billed monthly', 'மாதந்தோறும்')}</p>
              <p className={`mt-2 text-sm text-ink-2 ${tf}`}>{ta ? tier.tagline_ta : tier.tagline_en}</p>
              <ul className="mt-6 grid gap-3 flex-1">
                {(ta ? tier.features_ta : tier.features_en).map((feat) => (
                  // A trailing ':' marks an "Everything in X, plus:" subhead, not a checklist item.
                  feat.endsWith(':')
                    ? <li key={feat} className={`text-[13px] font-medium text-primary-ink ${tf}`}>{feat}</li>
                    : <li key={feat} className={`flex items-start gap-2.5 text-sm text-ink-2 ${tf}`}><span className="mt-0.5 text-primary shrink-0"><Icon.check /></span>{feat}</li>
                ))}
              </ul>
              <a href={signup_url} className={`mt-7 inline-flex items-center justify-center px-4 py-3 rounded-xl text-sm font-medium transition no-underline ${tf} ${tier.highlight ? 'lp-cta bg-primary text-white' : 'border border-line hover:border-primary text-ink-2 hover:text-primary'}`}>{t('Choose ' + tier.name_en, tier.name_ta + ' தேர்வு')}</a>
            </div>
          ))}
        </div>
        {/* Enterprise strip */}
        <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-2xl border border-line bg-surface px-7 py-6">
          <div>
            <h3 className={`text-lg font-semibold text-primary-ink ${ta ? 'font-tamil' : ''}`}>{t('Enterprise', 'எண்டர்பிரைஸ்')}</h3>
            <p className={`mt-1 text-sm text-ink-2 ${tf}`}>{t('500+ chits, multiple branches, custom integrations and an SLA. Everything in Scale, tailored to you.', '500+ சீட்டுகள், பல கிளைகள், தனிப்பயன் ஒருங்கிணைப்பு & SLA. அளவீட்டில் உள்ள அனைத்தும், உங்களுக்கேற்ப.')}</p>
          </div>
          <a href="mailto:hello@chitdesk.com" className={`shrink-0 inline-flex items-center justify-center px-5 py-3 rounded-xl text-sm font-medium border border-line hover:border-primary text-ink-2 hover:text-primary transition no-underline ${tf}`}>{t('Talk to us', 'எங்களை தொடர்பு கொள்ளுங்கள்')}</a>
        </div>
        {/* Comparison table */}
        <div className="mt-14 overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-line">
                <th className={`py-3 pr-4 text-left font-medium text-ink-3 ${tf}`}>{t('Compare plans', 'திட்டங்களை ஒப்பிடுக')}</th>
                {TIERS.map((tier) => (
                  <th key={tier.name_en} className={`py-3 px-3 text-center font-semibold text-primary-ink ${ta ? 'font-tamil' : ''}`}>{ta ? tier.name_ta : tier.name_en}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE.map((row) => (
                <tr key={row.label_en} className="border-b border-line/60">
                  <td className={`py-3 pr-4 text-ink-2 ${tf}`}>{ta ? row.label_ta : row.label_en}</td>
                  {row.cells.map((cell, i) => (
                    <td key={i} className="py-3 px-3 text-center">
                      {cell === 1 ? <span className="inline-flex text-primary"><Icon.check /></span>
                        : cell === 0 ? <span className="text-ink-3">–</span>
                        : <span className={`text-ink ${tf}`}>{cell}</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="border-t border-line bg-surface/60">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-20 sm:py-24">
          <div className="text-center mb-12">
            <div className="lp-eyebrow text-primary mb-3">FAQ</div>
            <h2 className={`${dsp} text-3xl sm:text-4xl text-primary-ink`} style={{ fontWeight: 600 }}>{t('Questions foremen actually ask.', 'நிறுவனர்கள் கேட்கும் கேள்விகள்.')}</h2>
          </div>
          <div className="divide-y divide-line">
            {FAQS.map((f) => (
              <details key={f.q_en} className="group py-5">
                <summary className={`flex items-center justify-between cursor-pointer list-none text-base font-medium text-ink ${ta ? 'font-tamil' : ''}`}>
                  {ta ? f.q_ta : f.q_en}
                  <span className="ml-4 text-ink-3 transition-transform group-open:rotate-90 shrink-0"><Icon.chevR /></span>
                </summary>
                <p className={`mt-3 text-sm text-ink-2 leading-relaxed ${tf}`}>{ta ? f.a_ta : f.a_en}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 py-20 sm:py-24">
        <div className="relative overflow-hidden rounded-3xl bg-primary-ink text-white px-6 sm:px-14 py-16 text-center">
          <div className="lp-aura" style={{ width: 460, height: 380, right: '8%', bottom: -160, background: 'radial-gradient(closest-side, rgba(63,122,82,0.55), transparent)' }} />
          <h2 className={`${dsp} text-3xl sm:text-5xl leading-tight`} style={{ fontWeight: 600 }}>{t('Run your next month on autopilot.', 'உங்கள் அடுத்த மாதத்தை தானியங்கியில் நடத்துங்கள்.')}</h2>
          <p className={`mt-4 text-white/75 max-w-xl mx-auto ${tf}`}>{t('Create your workspace in two minutes. Keep your paper book as long as you like.', 'இரண்டே நிமிடத்தில் வேலையகத்தை உருவாக்குங்கள். பதிவேட்டை எவ்வளவு காலமும் வைத்திருங்கள்.')}</p>
          <a href={signup_url} className={`lp-cta inline-flex items-center gap-2 mt-8 px-7 py-3.5 rounded-xl bg-white text-primary-ink font-semibold text-sm no-underline ${tf}`}>{t('Create your workspace', 'வேலையகத்தை உருவாக்குங்கள்')}<Icon.chevR /></a>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-line">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-5">
            <div className="flex items-center gap-2.5">
              <BrandMark size={30} />
              <span className="text-base font-semibold tracking-tight text-primary-ink">ChitDesk</span>
            </div>
            <div className={`flex items-center gap-6 text-sm text-ink-2 ${tf}`}>
              <a href="#pricing" onClick={scroll_to('pricing')} className="hover:text-primary transition no-underline">{t('Pricing', 'கட்டணம்')}</a>
              <a href="/docs" className="hover:text-primary transition no-underline">{t('Docs', 'ஆவணங்கள்')}</a>
              <a href={signup_url} className="hover:text-primary transition no-underline">{t('Get started', 'தொடங்கு')}</a>
            </div>
            <div className="flex items-center gap-3 text-xs text-ink-3">
              <span>© {new Date().getFullYear()} ChitDesk</span>
              <span className="font-tamil">தமிழ்நாட்டிற்காக கட்டப்பட்டது</span>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-line/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className={`text-xs text-ink-3 max-w-xl ${tf}`}>{t('ChitDesk is software for managing your business records. ChitDesk does not operate a chit fund and does not collect, pool or hold member deposits.', 'ChitDesk என்பது உங்கள் வணிகப் பதிவுகளை நிர்வகிக்கும் மென்பொருள். ChitDesk சீட்டு நிறுவனத்தை நடத்துவதில்லை; உறுப்பினர் வைப்புத்தொகையை வசூலிக்கவோ சேமிக்கவோ இல்லை.')}</p>
            <div className="flex flex-wrap items-center gap-5 text-xs text-ink-2 shrink-0">
              <a href="/terms" className="hover:text-primary transition no-underline">{t('Terms', 'விதிமுறைகள்')}</a>
              <a href="/privacy" className="hover:text-primary transition no-underline">{t('Privacy', 'தனியுரிமை')}</a>
              <a href="/refund" className="hover:text-primary transition no-underline">{t('Refunds', 'பணத்திரும்பல்')}</a>
              <a href="/contact" className="hover:text-primary transition no-underline">{t('Contact', 'தொடர்பு')}</a>
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
}
